import { askConfirmation } from 'components/common/Confirm';
import type ApplicationModel from 'components/framework/ApplicationModel';
import {
  type Epic,
  type Feature,
  Page,
  type Requirement,
} from 'components/framework/models';
import AhaAnalytics from 'javascripts/analytics';
import { modelTerminology } from 'javascripts/terminology';
import RecordAction from '../RecordAction';
import type { RecordUnion } from 'components/framework/models/generated/types';

type RecordType = RecordUnion | Page;

export class DeleteRecordAction extends RecordAction<RecordType> {
  recordTypes = ['Page', 'Feature', 'Requirement', 'Epic'];
  menuItemType = 'danger' as const;

  get title() {
    const compatibleItems = this.selectedRecords.filter(r =>
      this.showForRecord(r)
    );
    if (compatibleItems.length > 0) {
      if (compatibleItems.length > 1) {
        return `Delete ${compatibleItems.length} records`;
      }

      const record = compatibleItems[0];
      if (record instanceof Page && record.isLink) {
        return 'Delete link';
      }

      return `Delete ${modelTerminology(record).toLowerCase()}`;
    }
    return 'Delete record';
  }

  get icon() {
    return 'fa-regular fa-trash-can';
  }

  showForRecord(record: ApplicationModel): record is RecordType {
    if (!super.showForRecord(record)) {
      return false;
    }

    // The user should only be able to delete records that belong to
    // the current workspace/team. E.g. they can't delete a roadmaps feature
    // from the team workflow board. Ignore for Page types.
    if (record.typename === 'Page') {
      return record.permissions?.destroy !== false;
    }

    const belongsToProjectOrTeam =
      record.project?.id === window.currentProject?.id ||
      record.team?.id === window.currentProject?.id;

    return belongsToProjectOrTeam && record.permissions?.destroy !== false;
  }

  showForSelectedRecords() {
    return this.selectedRecords.every(record => this.showForRecord(record));
  }

  _trackAnalyticsForRecord(record) {
    if (record.typename === 'Page') {
      if (record.isFolder) {
        AhaAnalytics.track(
          'Feature-7212991088368439905',
          'User deletes folder'
        );
      } else {
        AhaAnalytics.track(
          'Feature-7131071994762461401',
          'User deletes documents',
          {
            'Document type': record.documentType,
          }
        );
      }
    }
  }

  async runForRecords(
    records: Array<Page | Feature | Requirement | Epic>,
    _data,
    controller
  ) {
    let prompt;
    if (records.length === 1) {
      const name = records[0].name;
      prompt = `Are you sure you want to delete ${
        name || `this ${modelTerminology(records[0]).toLowerCase()}`
      }?`;
    } else {
      prompt = `Are you sure you want to delete ${records.length} records?`;
    }

    if (await askConfirmation({ body: prompt })) {
      await Promise.all(
        records.map(async record => {
          const destroyPromise = record.destroy({
            args: { __reactivelyUpdate: true },
          });
          this._trackAnalyticsForRecord(record);
          return destroyPromise;
        })
      );

      records.forEach(record =>
        document.dispatchEvent(
          new CustomEvent('aha.destroy', { detail: record })
        )
      );
    }
  }
}
